import type { AccessGrant, ApiEnvelope, ApiProblemBody, Surface } from "@anyseal/contracts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  authenticated?: boolean;
  retryAuthentication?: boolean;
  idempotencyKey?: string;
}

export interface AnysealClientOptions {
  baseUrl: string;
  surface: Surface;
  fetch?: typeof globalThis.fetch;
  onAuthenticationExpired?: () => void;
}

export class AnysealClient {
  private accessToken = "";
  private refreshPromise: Promise<boolean> | undefined;
  private readonly accessTokenStorageKey: string;
  private readonly sessionStorage: Storage | null;
  private readonly fetcher: typeof globalThis.fetch;
  private readonly root: string;

  constructor(private readonly options: AnysealClientOptions) {
    this.root = options.baseUrl.replace(/\/$/, "");
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.accessTokenStorageKey = `anyseal.api-client.${options.surface}.access-token`;
    this.sessionStorage = this.resolveSessionStorage();
    this.accessToken = this.sessionStorage?.getItem(this.accessTokenStorageKey) ?? "";
  }

  setAccessToken(token: string): void {
    this.accessToken = token;
    this.persistAccessToken(token);
  }

  clearAccessToken(): void {
    this.accessToken = "";
    this.persistAccessToken("");
  }

  hasAccessToken(): boolean {
    return this.accessToken.length > 0;
  }

  async restoreSession(): Promise<boolean> {
    if (!this.accessToken) {
      return false;
    }
    if (await this.refresh(false)) return true;
    return this.hasAccessToken();
  }

  async logout(): Promise<void> {
    try {
      await this.request(`/api/${this.options.surface}/v1/auth/logout`, {
        method: "POST",
        authenticated: false,
        retryAuthentication: false,
      });
    } finally {
      this.clearAccessToken();
    }
  }

  get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request(path, { ...options, method: "GET" });
  }

  post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request(path, { ...options, method: "POST", body });
  }

  put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request(path, { ...options, method: "PUT", body });
  }

  patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request(path, { ...options, method: "PATCH", body });
  }

  delete<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request(path, { ...options, method: "DELETE", body });
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const authenticated = options.authenticated ?? true;
    const headers = new Headers(options.headers);
    headers.set("Accept", "application/json");
    if (authenticated && this.accessToken) headers.set("Authorization", `Bearer ${this.accessToken}`);
    if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);
    let body: BodyInit | undefined;
    if (options.body !== undefined) {
      if (options.body instanceof FormData || typeof options.body === "string") body = options.body;
      else {
        headers.set("Content-Type", "application/json");
        body = JSON.stringify(options.body);
      }
    }
    const response = await this.fetcher(`${this.root}${path}`, {
      ...options,
      headers,
      body,
      credentials: "include",
    });
    if (response.status === 401 && authenticated && (options.retryAuthentication ?? true) && await this.refresh(true)) {
      return this.request<T>(path, { ...options, retryAuthentication: false });
    }
    if (response.status === 204) return undefined as T;
    const payload = await safeJSON(response);
    if (!response.ok) throw problemFrom(response, payload);
    if (isEnvelope<T>(payload)) return payload.data;
    return payload as T;
  }

  private async refresh(allowSessionInvalidation = true): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = (async () => {
      try {
        const response = await this.fetcher(`${this.root}/api/${this.options.surface}/v1/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const payload = await safeJSON(response);
        if (!response.ok) {
          if (allowSessionInvalidation) {
            this.clearAccessToken();
            this.options.onAuthenticationExpired?.();
          }
          return false;
        }
        const grant = isEnvelope<AccessGrant>(payload) ? payload.data : payload as AccessGrant;
        this.setAccessToken(grant.access_token);
        return true;
      } catch {
        if (allowSessionInvalidation) this.clearAccessToken();
        return false;
      } finally {
        this.refreshPromise = undefined;
      }
    })();
    return this.refreshPromise;
  }

  private resolveSessionStorage(): Storage | null {
    if (typeof globalThis === "undefined") {
      return null;
    }
    try {
      const storage = globalThis.sessionStorage;
      // Keep behavior safe when browser storage is disabled or inaccessible.
      storage.getItem("__anyseal_session_storage_probe__");
      return storage;
    } catch {
      return null;
    }
  }

  private persistAccessToken(token: string): void {
    if (!this.sessionStorage) {
      return;
    }
    try {
      if (token.length > 0) this.sessionStorage.setItem(this.accessTokenStorageKey, token);
      else this.sessionStorage.removeItem(this.accessTokenStorageKey);
    } catch {
      // Ignore persistence failures intentionally; in-memory token flow still works.
    }
  }
}

export const createIdempotencyKey = (): string => crypto.randomUUID();

export const queryString = (values: Record<string, string | number | boolean | undefined>): string => {
  const query = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== "") query.set(key, String(value));
  });
  const text = query.toString();
  return text ? `?${text}` : "";
};

const isEnvelope = <T>(value: unknown): value is ApiEnvelope<T> => {
  return typeof value === "object" && value !== null && "data" in value && "request_id" in value;
};

const safeJSON = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return undefined;
  try { return JSON.parse(text) as unknown; } catch { return undefined; }
};

const problemFrom = (response: Response, payload: unknown): ApiError => {
  const value = payload as Partial<ApiProblemBody> | undefined;
  const requestId = value?.request_id ?? response.headers.get("X-Request-ID") ?? "";
  return new ApiError(
    response.status,
    value?.error?.code ?? "request_failed",
    value?.error?.message ?? "The request could not be completed.",
    requestId,
    value?.error?.details,
  );
};
